"use client";
import { useState, useEffect } from "react";
import Image from "next/image";
import styles from "./blur-image.module.scss";
import { ImageNew } from "@/app/types/new/project";

interface BlurredImageProps {
  image: ImageNew;
  className?: string;
  priority?: boolean;
  fill?: boolean;
  imgSize: "thumbnail" | "small" | "medium" | "large";
  quality?: number;
  alt?: string;
  unoptimized?: boolean;
  sizes?: string;
}

export const BlurredImage = ({
  image,
  className = "",
  priority = false,
  fill = true,
  imgSize = "large",
  quality = 75,
  alt = "",
  unoptimized = false,
  sizes,
}: BlurredImageProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentSrc, setCurrentSrc] = useState("");

  // Get the appropriate image URL based on size and loading state
  const getFullUrl = () => {
    if (isLoading) {
      return image.formats?.thumbnail.url;
    }
    switch (imgSize) {
      case "thumbnail":
        return image.formats?.thumbnail.url ?? image.formats?.small?.url;
      case "small":
        return (
          image.formats?.small?.url ?? image.formats?.medium?.url ?? image.url
        );
      case "medium":
        return image.formats?.medium?.url ?? image.url;
      case "large":
        return image.url;
    }
  };

  const baseUrl =
    process.env.STRAPI_URL || process.env.NEXT_PUBLIC_STRAPI_URL || "";
  const fullUrl = baseUrl + getFullUrl();

  // Reset loading state when image source changes
  useEffect(() => {
    if (currentSrc !== fullUrl) {
      // setIsLoading(true);
      setCurrentSrc(fullUrl);
    }
  }, [fullUrl, currentSrc]);

  // Handle loading complete
  const handleLoadComplete = () => {
    setIsLoading(false);
  };

  if (
    image.url.toLowerCase().includes(".mov") ||
    image.url.toLowerCase().includes(".mp4")
  ) {
    return (
      <div
        className={`${styles.imageWrapper} ${className}`}
        style={{ position: "relative" }} // Ensure container has relative positioning for fill mode
      >
        <video
          style={{ objectFit: "cover" }}
          autoPlay
          muted
          controls
          src={baseUrl + image.url}
          className={styles.image}
        />
      </div>
    );
  }
  return (
    <div
      className={`${styles.imageWrapper} ${className}`}
      style={{ position: "relative" }} // Ensure container has relative positioning for fill mode
    >
      {fullUrl && (
        <Image
          src={fullUrl}
          alt={image.alternativeText ?? alt ?? ""}
          className={`
            ${styles.image}
            ${isLoading ? styles.imageLoading : styles.imageLoaded}
            transition-opacity duration-300 ease-in-out
          `}
          onLoad={handleLoadComplete}
          onLoadingComplete={handleLoadComplete}
          quality={quality}
          priority={priority}
          unoptimized={
            imgSize === "thumbnail" || imgSize === "small" || unoptimized
          }
          // sizes={sizes}
          {...(fill
            ? {
                fill: true,
              }
            : {
                width: image.width,
                height: image.height,
              })}
          // Add blur placeholder for better loading experience
          placeholder="blur"
          blurDataURL={`${baseUrl}${image.formats?.thumbnail.url}`}
        />
      )}
    </div>
  );
};
